import { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import escape from 'lodash/escape';
import isEqual from 'lodash/isEqual';
import Highcharts from 'highcharts';
import patternFill from 'highcharts/modules/pattern-fill';
import { propertiesSelector } from 'scripts/redux/selectors/properties';
import PropertyService from 'scripts/services/PropertyService';
import { FiChevronDown, FiDownload, FiSearch, FiX } from 'react-icons/fi';
import WidgetPanel from 'src/components/dashboard/common/WidgetPanel';
import Highchart from 'src/third-party/highchart/Highchart';
import AdvisorTableEmptyState from './AdvisorTableEmptyState';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import history from 'src/browser/history';
import { propertyUrl } from 'src/utils/pageUrls';
import {
  chartAccent01,
  chartAccent02,
  chartAccent03,
  chartAccent04,
  chartAccent05,
  chartAccent06,
  grey100,
  purple,
  purple100,
} from 'src/themes/brickx/colors';
import { renderCode } from 'src/settings/properties';
import { downloadCsv, formatCount } from './clientDetail/helpers';
import {
  getFilteredPortfolioHoldings,
  getPortfolioInvestmentType,
  getPortfolioOwnership,
} from './advisorClientDetailData';
import {
  DataToolbar,
  FilterSelect,
  OrdersExport,
  OrdersSelectField,
  SearchField,
} from './clientDetail/styles';
import { useActivityLayout } from './clientDetail/hooks';

if (Highcharts.SVGRenderer) patternFill(Highcharts);

export const buildClientOwnershipExportRows = (clients, investments) => {
  const rows = [
    [
      'Client email',
      ...investments.map((investment) => investment.propertyCode),
    ],
  ];
  if (!investments.length) return rows;
  const seenClients = new Set();
  clients.forEach((client) => {
    if (client.id) {
      if (seenClients.has(client.id)) return;
      seenClients.add(client.id);
    }
    rows.push([
      client.email || '',
      ...investments.map((investment) => {
        const bricks = Number(
          (client.propertiesOwnership || {})[investment.investmentCode],
        );
        return Number.isFinite(bricks) && bricks > 0 ? bricks : 0;
      }),
    ]);
  });
  return rows;
};

const OwnershipBody = styled.div`
  min-width: 0;
`;

const OwnershipToolbar = styled(DataToolbar)`
  && {
    padding: 0;
    margin-bottom: 24px;
    gap: ${gridUnit * 2}px;
    grid-template-columns: minmax(0, 1fr);
  }

  &&[data-layout='split'] {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  &&[data-layout='wide'] {
    grid-template-columns: minmax(0, 1fr) repeat(3, minmax(0, 176px));
  }

  ${SearchField} {
    min-width: 0;
    max-width: none;

    svg {
      color: var(--bx-color---grey-800);
    }
  }

  ${SearchField} input,
  ${FilterSelect} {
    min-width: 0;
    max-width: 100%;
    height: 40px;
    font-size: 1.4rem;
  }

  ${FilterSelect} {
    appearance: none;
    color: var(--bx-color---grey-700);
  }

  ${SearchField} input:focus,
  ${FilterSelect}:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
    outline: none;
  }
`;

const ClientSearch = styled.div`
  position: relative;
  min-width: 0;

  ${SearchField} {
    max-width: none;
  }

  input {
    min-width: 0;
    padding-right: 36px;
  }
`;

const ClearClient = styled.button`
  position: absolute;
  top: 4px;
  right: 5px;
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--bx-color---grey-700);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--bx-color---purple-400);
  }
`;

const ClientSuggestions = styled.div`
  position: absolute;
  top: 44px;
  right: 0;
  left: 0;
  z-index: 10;
  border: 1px solid var(--bx-color---grey-300);
  border-radius: 4px;
  background: var(--bx-color---surface);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  [role='option'] {
    display: block;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    background: transparent;
    color: var(--bx-color---grey-800);
    font-family: inherit;
    font-size: 1.4rem;
    text-align: left;
    overflow-wrap: anywhere;
    cursor: pointer;
  }

  [role='option']:hover,
  [aria-selected='true'] {
    background: var(--bx-color---purple-50);
  }

  [role='status'] {
    padding: 10px 12px;
    font-size: 16px;
  }
`;

const clientLabel = (client) =>
  [client.name, client.email].filter(Boolean).join(' \u2014 ');

const OwnershipSelect = styled(FilterSelect)`
  min-width: 0;
  appearance: none;
`;

const OwnershipCard = styled(({ className, ...props }) => (
  <WidgetPanel rootClassName={className} {...props} />
))`
  min-width: 0;

  & > div {
    min-width: 0;
  }

  & > div:last-child {
    padding-top: 0;
  }

  & > div:first-child > :first-child {
    flex: 1 1 auto;
    min-width: 0;
    color: var(--bx-color---grey-800);
    font-size: 20px;
  }

  & > div:first-child {
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }
`;

const OwnershipMessage = styled(AdvisorTableEmptyState)`
  padding-bottom: 0;
`;

export const ClientOwnershipPanel = ({
  ownership,
  properties = [],
  clients = [],
}) => {
  const { containerRef, filterLayout } = useActivityLayout();
  const [catalog, setCatalog] = useState(null);
  const [issuedFinancials, setIssuedFinancials] = useState([]);
  const [investmentType, setInvestmentType] = useState('all');
  const [state, setState] = useState('all');
  const [sort, setSort] = useState('propertyCode:asc');
  const [clientSelection, setClientSelection] = useState(null);
  const [clientQuery, setClientQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const selectedClient =
    clientSelection &&
    clients.find(
      (client) =>
        client === clientSelection ||
        (clientSelection.id && client.id === clientSelection.id),
    );
  const query = clientQuery.trim().toLowerCase();
  const seenSuggestions = new Set();
  const suggestions = clients
    .filter((client) => {
      if (client.id) {
        if (seenSuggestions.has(client.id)) return false;
        seenSuggestions.add(client.id);
      }
      return (
        query &&
        [client.name, client.email].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(query),
        )
      );
    })
    .slice(0, 5);
  const showSuggestions = searchOpen && !!query;
  const selectClient = (client) => {
    setClientSelection(client);
    setClientQuery('');
    setSearchOpen(false);
    setActiveSuggestion(-1);
  };
  const hasHoldings = !!(ownership && ownership.length);
  const needsCatalog = !!(ownership && ownership.length && !properties.length);

  useEffect(() => {
    if (!hasHoldings) return undefined;
    let cancelled = false;
    PropertyService.getAllFinancials()
      .then((response) => {
        if (!cancelled && Array.isArray(response)) {
          setIssuedFinancials(response);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hasHoldings]);

  useEffect(() => {
    if (!needsCatalog) return undefined;
    let cancelled = false;
    PropertyService.getPublishedProperties()
      .then((response) => {
        if (!cancelled) setCatalog(Array.isArray(response) ? response : []);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [needsCatalog]);

  const propertyByCode = new Map(
    (properties.length ? properties : catalog || []).map((property) => [
      property.propertyCode,
      property,
    ]),
  );
  const totalBricks = (ownership || []).reduce(
    (total, investment) => total + investment.totalBricks,
    0,
  );
  const financialsByCode = new Map(
    issuedFinancials.map((financials) => [financials.propertyCode, financials]),
  );
  const investments = (ownership || []).map((investment) => {
    const property = propertyByCode.get(investment.investmentCode) || {};
    const address = property.address || {};
    const propertyState = property.state || address.state || '';
    const financials =
      financialsByCode.get(investment.investmentCode) ||
      property.financials ||
      {};
    return {
      ...investment,
      propertyCode: renderCode(investment.investmentCode),
      investmentType: getPortfolioInvestmentType(property),
      state: propertyState,
      location:
        [property.suburb || address.suburb, propertyState]
          .filter(Boolean)
          .join(', ') ||
        property.name ||
        '',
      numberBricks: financials.numberBricks,
      ownership: getPortfolioOwnership(
        investment.totalBricks,
        financials.numberBricks,
      ),
    };
  });
  const [sortBy, sortDirection] = sort.split(':');
  const filteredInvestments = getFilteredPortfolioHoldings(investments, {
    state,
    investmentType,
    sortBy,
    sortDirection,
  });
  const states = Array.from(
    new Set(investments.map((investment) => investment.state).filter(Boolean)),
  ).sort();
  const exportRows = buildClientOwnershipExportRows(
    clients,
    filteredInvestments,
  );
  const propertiesLoading = needsCatalog && catalog === null;
  const chartData = filteredInvestments.map((investment, index) => {
    const owned = Number(
      ((selectedClient && selectedClient.propertiesOwnership) || {})[
        investment.investmentCode
      ],
    );
    const selectedBricks = Number.isFinite(owned) && owned > 0 ? owned : 0;
    return {
      x: index,
      name: investment.propertyCode,
      investmentCode: investment.investmentCode,
      y: investment.totalBricks,
      location: investment.location,
      totalBricks: investment.totalBricks,
      selectedBricks,
      selectedPercentage: new Intl.NumberFormat('en-AU', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(selectedBricks / investment.totalBricks),
      ownershipPercentage:
        investment.ownership === null
          ? null
          : new Intl.NumberFormat('en-AU', {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            }).format(investment.ownership),
    };
  });
  const previousChart = useRef(null);
  const chartState = {
    data: chartData,
    client: selectedClient
      ? {
          id: selectedClient.id,
          name: selectedClient.name,
          email: selectedClient.email,
        }
      : null,
  };
  const chartChanged = !isEqual(previousChart.current, chartState);
  useEffect(() => {
    previousChart.current = chartState;
  });

  return (
    <OwnershipCard
      title="Portfolio Breakdown"
      titleAlign="left"
      action={
        <OrdersExport
          type="button"
          aria-label="Export portfolio breakdown"
          disabled={exportRows.length <= 1}
          onClick={() =>
            downloadCsv('adviser-portfolio-breakdown.csv', exportRows)
          }
        >
          <FiDownload aria-hidden="true" />
          Export
        </OrdersExport>
      }
      testRef="advisor-client-ownership"
      contentTestRef="advisor-client-ownership-content"
    >
      <OwnershipBody ref={containerRef}>
        {ownership === null ? (
          <OwnershipMessage>Client holdings are unavailable.</OwnershipMessage>
        ) : !totalBricks ? (
          <OwnershipMessage>No client holdings yet.</OwnershipMessage>
        ) : (
          <Fragment>
            <OwnershipToolbar data-layout={filterLayout}>
              <ClientSearch>
                <SearchField>
                  <FiSearch aria-hidden="true" />
                  <input
                    type="text"
                    role="combobox"
                    aria-label="Client Search"
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions}
                    aria-controls="portfolio-breakdown-clients"
                    aria-activedescendant={
                      showSuggestions && activeSuggestion >= 0
                        ? `portfolio-breakdown-client-${activeSuggestion}`
                        : undefined
                    }
                    autoComplete="off"
                    placeholder="Search clients..."
                    title={
                      selectedClient ? clientLabel(selectedClient) : undefined
                    }
                    value={
                      searchOpen
                        ? clientQuery
                        : selectedClient
                          ? clientLabel(selectedClient)
                          : clientQuery
                    }
                    onFocus={() => {
                      setSearchOpen(true);
                      if (selectedClient) {
                        setClientQuery(
                          selectedClient.name || selectedClient.email || '',
                        );
                      }
                    }}
                    onBlur={() => {
                      setSearchOpen(false);
                      setClientQuery('');
                      setActiveSuggestion(-1);
                    }}
                    onChange={(event) => {
                      setClientQuery(event.target.value);
                      setSearchOpen(true);
                      setActiveSuggestion(-1);
                    }}
                    onKeyDown={(event) => {
                      const key = event.key;
                      if (key === 'ArrowDown' || key === 'ArrowUp') {
                        event.preventDefault();
                        setSearchOpen(true);
                        if (suggestions.length) {
                          setActiveSuggestion((current) =>
                            key === 'ArrowDown'
                              ? (current + 1) % suggestions.length
                              : (current <= 0 ? suggestions.length : current) -
                                1,
                          );
                        }
                      } else if (key === 'Enter') {
                        event.preventDefault();
                        if (showSuggestions && suggestions[activeSuggestion]) {
                          selectClient(suggestions[activeSuggestion]);
                        }
                      } else if (key === 'Escape') {
                        setSearchOpen(false);
                        setClientQuery('');
                        setActiveSuggestion(-1);
                      }
                    }}
                  />
                </SearchField>
                {selectedClient && (
                  <ClearClient
                    type="button"
                    aria-label="Clear client selection"
                    title="Clear client selection"
                    onClick={() => selectClient(null)}
                  >
                    <FiX aria-hidden="true" />
                  </ClearClient>
                )}
                {showSuggestions && (
                  <ClientSuggestions>
                    <div
                      id="portfolio-breakdown-clients"
                      role="listbox"
                      aria-label="Connected clients"
                    >
                      {suggestions.map((client, index) => (
                        <button
                          key={client.id || index}
                          id={`portfolio-breakdown-client-${index}`}
                          type="button"
                          role="option"
                          aria-selected={activeSuggestion === index}
                          tabIndex={-1}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => selectClient(client)}
                        >
                          {clientLabel(client)}
                        </button>
                      ))}
                    </div>
                    {!suggestions.length && (
                      <div role="status">No clients found.</div>
                    )}
                  </ClientSuggestions>
                )}
              </ClientSearch>
              <OrdersSelectField>
                <OwnershipSelect
                  aria-label="Filter portfolio breakdown by investment type"
                  value={investmentType}
                  disabled={propertiesLoading}
                  onChange={(event) => setInvestmentType(event.target.value)}
                >
                  <option value="all">All types</option>
                  <option value="property">Property</option>
                  <option value="mortgage">Mortgage</option>
                  <option value="financial_asset">Financial assets</option>
                  <option value="development">Development</option>
                </OwnershipSelect>
                <FiChevronDown aria-hidden="true" />
              </OrdersSelectField>
              <OrdersSelectField>
                <OwnershipSelect
                  aria-label="Filter portfolio breakdown by state"
                  value={state}
                  disabled={propertiesLoading}
                  onChange={(event) => setState(event.target.value)}
                >
                  <option value="all">All states</option>
                  {states.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </OwnershipSelect>
                <FiChevronDown aria-hidden="true" />
              </OrdersSelectField>
              <OrdersSelectField>
                <OwnershipSelect
                  aria-label="Sort portfolio breakdown"
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                >
                  <option value="propertyCode:asc">
                    Investment code (A-Z)
                  </option>
                  <option value="propertyCode:desc">
                    Investment code (Z-A)
                  </option>
                  <option value="ownership:asc">Ownership (low to high)</option>
                  <option value="ownership:desc">
                    Ownership (high to low)
                  </option>
                </OwnershipSelect>
                <FiChevronDown aria-hidden="true" />
              </OrdersSelectField>
            </OwnershipToolbar>
            {!filteredInvestments.length ? (
              <OwnershipMessage>
                No investments match the selected filters.
              </OwnershipMessage>
            ) : (
              <Highchart
                hotReload={chartChanged}
                config={{
                  credits: { enabled: false },
                  chart: {
                    type: 'bar',
                    height: 48 * (filteredInvestments.length + 1),
                    style: { fontSize: '12px' },
                    events: {
                      render: function () {
                        if (!selectedClient) return;
                        this.series[0].points.forEach((point) => {
                          if (!point.graphic || !point.shapeArgs) return;
                          const pattern =
                            point.ownershipPattern ||
                            this.renderer.addPattern({
                              ...this.series[0].options.color.pattern,
                              id: `ownership-reference-${this.index}-${point.index}`,
                            });
                          const shape = point.shapeArgs;
                          pattern.attr({
                            x: shape.x + (shape.width - 8) / 2,
                            y: shape.y + (shape.height - 8) / 2,
                          });
                          point.ownershipPattern = pattern;
                          point.graphic.attr({
                            fill: `url(${this.renderer.url}#${pattern.id})`,
                          });
                        });
                      },
                    },
                  },
                  title: false,
                  legend: { enabled: false },
                  xAxis: {
                    type: 'category',
                    categories: chartData.map((point) => point.name),
                    tickLength: 0,
                    labels: { style: { fontSize: '12px' } },
                  },
                  yAxis: {
                    title: false,
                    allowDecimals: false,
                    labels: {
                      style: { fontSize: '12px' },
                      formatter: function () {
                        return formatCount(this.value);
                      },
                    },
                  },
                  plotOptions: {
                    series: {
                      grouping: !selectedClient,
                      animation: true,
                      borderWidth: 0,
                      cursor: 'pointer',
                      point: {
                        events: {
                          click: function () {
                            history.push(
                              propertyUrl(this.options.investmentCode),
                            );
                          },
                        },
                      },
                      dataLabels: {
                        enabled: true,
                        style: {
                          color: 'black',
                          textOutline: 'none',
                          fontSize: '12px',
                        },
                        formatter: function () {
                          if (selectedClient) {
                            const point = this.point.options;
                            return `${formatCount(point.selectedBricks)} / ${formatCount(point.totalBricks)} bricks (${point.selectedPercentage})`;
                          }
                          return `${formatCount(this.y)} bricks <span style="opacity: 0.5;">${
                            this.point.options.ownershipPercentage || '\u2014'
                          }</span>`;
                        },
                      },
                    },
                  },
                  tooltip: {
                    style: { fontSize: '14px' },
                    headerFormat: '<b>{point.key}</b><br/>',
                    pointFormatter: function () {
                      const location = this.options.location;
                      if (selectedClient) {
                        const point = this.options;
                        return `${escape(clientLabel(selectedClient))}<br/>Selected client: ${formatCount(point.selectedBricks)} bricks<br/>All clients: ${formatCount(point.totalBricks)} bricks<br/>${point.selectedPercentage} of all clients' holdings`;
                      }
                      return `${location ? `${escape(location)}<br/><br/>` : ''}${formatCount(
                        this.y,
                      )} bricks<br/>${
                        this.options.ownershipPercentage
                          ? `${this.options.ownershipPercentage} ownership`
                          : 'Ownership unavailable'
                      }`;
                    },
                  },
                  series: [
                    {
                      name: 'All clients',
                      colorByPoint: !selectedClient,
                      zIndex: 0,
                      color: selectedClient
                        ? {
                            pattern: {
                              path: {
                                d: 'M 3.5 4 L 4.5 4',
                                fill: grey100,
                                stroke: purple100,
                                strokeWidth: 2,
                              },
                              width: 8,
                              height: 8,
                            },
                          }
                        : undefined,
                      cursor: 'pointer',
                      colors: [
                        chartAccent01,
                        chartAccent02,
                        chartAccent03,
                        chartAccent04,
                        chartAccent05,
                        chartAccent06,
                      ],
                      data: chartData,
                    },
                    ...(selectedClient
                      ? [
                          {
                            name: clientLabel(selectedClient),
                            color: purple,
                            zIndex: 1,
                            dataLabels: { enabled: false },
                            data: chartData.map((point) => ({
                              ...point,
                              y: point.selectedBricks,
                            })),
                          },
                        ]
                      : []),
                  ],
                }}
              />
            )}
          </Fragment>
        )}
      </OwnershipBody>
    </OwnershipCard>
  );
};

ClientOwnershipPanel.propTypes = {
  ownership: PropTypes.arrayOf(
    PropTypes.shape({
      investmentCode: PropTypes.string.isRequired,
      totalBricks: PropTypes.number.isRequired,
    }),
  ),
  properties: PropTypes.array,
  clients: PropTypes.array,
};

export default connect(propertiesSelector)(ClientOwnershipPanel);
